import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Simplified state: user can be null (loading), false (not authenticated), or object (authenticated)
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false); // Only for explicit actions
  const [error, setError] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  // Helper to merge profile data with user
  const finalUser = useMemo(() => {
    if (!user || user === false) return user;
    return profile ? { ...user, ...profile } : user;
  }, [user, profile]);

  // Load profile data when user changes
  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return;
    }

    try {
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error && error.code !== 'PGRST116') { // Ignore "not found" errors for new users
        console.error("Profile fetch error:", error);
        return;
      }

      setProfile(profileData);
    } catch (error) {
      console.error("Profile load error:", error);
    }
  }, []);

  // Initialize auth and set up listener
  useEffect(() => {
    let mounted = true;

    // Get initial session - don't use cached if we have OAuth tokens
    const initAuth = async () => {
      try {
        // Check for OAuth flow
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasOAuthTokens = hashParams.get('access_token');

        let session;
        if (hasOAuthTokens) {
          // For OAuth flows, get fresh session
          const { data: { session: freshSession } } = await supabase.auth.getSession();
          session = freshSession;
        } else {
          // For normal flows, use cached session
          const { data: { session: cachedSession } } = await supabase.auth.getSession();
          session = cachedSession;
        }

        if (mounted) {
          if (session?.user) {
            setUser(session.user);
            loadProfile(session.user.id);
          } else {
            setUser(false);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        if (mounted) {
          setUser(false);
        }
      }
    };

    initAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // console.log("Auth event:", event, !!session?.user);
        
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          loadProfile(session.user.id);
        } else {
          setUser(false);
          setProfile(null);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [loadProfile]);

  // Auth actions
  const signUp = useCallback(async (email, password, name) => {
    try {
      setActionLoading(true);
      setError(null);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
          emailRedirectTo: `${window.location.origin}/auth-redirect`
        }
      });

      if (error) throw error;
      return { data, error: null, confirmationEmailSent: !data.session };
    } catch (error) {
      console.error("Signup error:", error);
      setError(error.message);
      return { data: null, error };
    } finally {
      setActionLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      setActionLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Sign in error:", error);
      setError(error.message);
      return { data: null, error };
    } finally {
      setActionLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth-redirect`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Google sign in error:", error);
      setError(error.message);
      return { success: false, error };
    } finally {
      setActionLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      // Clear local state first for immediate UI feedback
      setUser(false);
      setProfile(null);
      
      const { error } = await supabase.auth.signOut();
      
      // Ignore session errors during signout
      if (error && !error.message.includes('session')) {
        throw error;
      }
      
      return { success: true };
    } catch (error) {
      console.error("Sign out error:", error);
      setError(error.message);
      return { success: false, error };
    } finally {
      setActionLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session?.user) {
        setUser(session.user);
        await loadProfile(session.user.id);
      } else {
        setUser(false);
        setProfile(null);
      }
      
      return { success: true };
    } catch (error) {
      console.error("Refresh user error:", error);
      return { success: false, error };
    }
  }, [loadProfile]);

  const completeOnboarding = useCallback(async (updates = {}) => {
    if (!finalUser) {
      throw new Error("Not signed in");
    }

    try {
      setActionLoading(true);
      setError(null);
      
      const { error } = await supabase
        .from("profiles")
        .update({ 
          ...updates, 
          onboarding_completed: true, 
          updated_at: new Date().toISOString() 
        })
        .eq("id", finalUser.id);

      if (error) throw error;
      
      // Refresh profile
      await loadProfile(finalUser.id);
      return { success: true };
    } catch (error) {
      console.error("Complete onboarding error:", error);
      setError(error.message);
      return { success: false, error };
    } finally {
      setActionLoading(false);
    }
  }, [finalUser, loadProfile]);

  const checkEmailConfirmation = useCallback(async (retries = 3) => {
    const checkWithRetry = async (retriesLeft) => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) throw error;

        const user = data.session?.user;
        const isConfirmed = !!(
          user?.email_confirmed_at ||
          user?.confirmed_at ||
          user?.app_metadata?.email_confirmed ||
          user?.app_metadata?.provider === 'google'
        );

        if (!isConfirmed && retriesLeft > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return checkWithRetry(retriesLeft - 1);
        }

        return { success: true, isConfirmed, user };
      } catch (error) {
        if (retriesLeft > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return checkWithRetry(retriesLeft - 1);
        }
        return { success: false, error: error.message };
      }
    };

    try {
      setActionLoading(true);
      setError(null);
      return await checkWithRetry(retries);
    } finally {
      setActionLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      setActionLoading(true);
      setError(null);
      
      const redirectUrl = `${window.location.origin}/auth-redirect?type=recovery&next=${encodeURIComponent('/account?tab=security')}`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        if (error.message.includes('rate limit')) {
          throw new Error('Too many reset attempts. Please wait a few minutes before trying again.');
        }
        throw error;
      }
      
      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message);
      return { success: false, error };
    } finally {
      setActionLoading(false);
    }
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    if (!finalUser) {
      throw new Error('Must be authenticated to update password');
    }

    try {
      setActionLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        if (error.message.includes('weak password')) {
          throw new Error('Password is too weak. Please choose a stronger password.');
        }
        if (error.message.includes('rate limit')) {
          throw new Error('Too many password update attempts. Please wait a few minutes before trying again.');
        }
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("Password update error:", error);
      setError(error.message);
      return { success: false, error };
    } finally {
      setActionLoading(false);
    }
  }, [finalUser]);

  // Computed values
  const ready = user !== null; // Auth is ready when user is not null
  const hasUser = !!finalUser && typeof finalUser === 'object';
  const isAuthenticated = hasUser;
  const hasActiveSubscription = !!(
    profile?.hasactivesubscription || 
    profile?.hasActiveSubscription
  );
  const isEmailConfirmed = !!(
    finalUser?.email_confirmed_at ||
    finalUser?.confirmed_at ||
    finalUser?.app_metadata?.email_confirmed ||
    finalUser?.app_metadata?.provider === 'google'
  );

  // Memoize context value
  const value = useMemo(() => ({
    user: finalUser,
    profile,
    loading: actionLoading, // Only loading during explicit actions
    isLoading: actionLoading,
    error,
    ready,
    hasUser,
    hasActiveSubscription,
    isEmailConfirmed,
    isAuthenticated,
    verifyingPayment,
    setVerifyingPayment,
    signUp,
    signIn,
    signOut,
    refreshUser,
    completeOnboarding,
    signInWithGoogle,
    checkEmailConfirmation,
    resetPassword,
    updatePassword,
  }), [
    finalUser,
    profile,
    actionLoading,
    error,
    ready,
    hasUser,
    hasActiveSubscription,
    isEmailConfirmed,
    isAuthenticated,
    verifyingPayment,
    signUp,
    signIn,
    signOut,
    refreshUser,
    completeOnboarding,
    signInWithGoogle,
    checkEmailConfirmation,
    resetPassword,
    updatePassword,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
